import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { Collection } from "../const/collections";
import { AcquisitionVerrous, DocumentVerrou, DocumentVerrouMode, DocumentVerrouType } from "../models/document-verrou";
import { LocalStorageService } from "./local-storage.service";

/**
 * Verrous d'édition des documents, comme dans Sage : on voit qui travaille sur un bon de commande, et
 * réception et correction du BC ne se font pas en même temps.
 *
 * Un verrou expire tout seul (10 minutes sans renouvellement) : l'écran le renouvelle tant que l'utilisateur
 * est actif, le libère en sortant, et l'administrateur peut le forcer. C'est un confort ; la garantie reste
 * le contrôle fait par le backend à l'enregistrement (code d'erreur BC_MODIFIE ou DOCUMENT_VERROUILLE).
 */
@Injectable({ providedIn: "root" })
export class DocumentVerrouService {
	private url = `${environment.apiURL}/${Collection.DOCUMENT_VERROU}`;

	/** Renouvellement à faire par les écrans tant que l'utilisateur est actif (le verrou dure 10 minutes). */
	static readonly INTERVALLE_RENOUVELLEMENT_MS = 2 * 60 * 1000;

	constructor(
		private http: HttpClient,
		private localStorage: LocalStorageService,
	) {}

	/** Prend ou renouvelle mes verrous. `refuses` liste les documents tenus par quelqu'un d'autre. */
	acquerir(documentType: DocumentVerrouType, documentIds: number[], mode: DocumentVerrouMode): Observable<{ data: AcquisitionVerrous }> {
		return this.http.put<{ data: AcquisitionVerrous }>(`${this.url}/acquerir`, { data: { documentType, documentIds, mode } });
	}

	liberer(documentType: DocumentVerrouType, documentIds: number[], mode?: DocumentVerrouMode): Observable<{ data: { liberes: number } }> {
		return this.http.put<{ data: { liberes: number } }>(`${this.url}/liberer`, { data: { documentType, documentIds, mode } });
	}

	/** Verrous actifs des documents (les miens sont marqués `estMoi`). */
	etat(documentType: DocumentVerrouType, documentIds: number[]): Observable<{ data: DocumentVerrou[] }> {
		return this.http.post<{ data: DocumentVerrou[] }>(`${this.url}/etat`, { data: { documentType, documentIds } });
	}

	/** Retire les verrous des autres utilisateurs (administrateur). */
	forcer(documentType: DocumentVerrouType, documentId: number): Observable<{ data: DocumentVerrou[] }> {
		return this.http.put<{ data: DocumentVerrou[] }>(`${this.url}/forcer`, { data: { documentType, documentIds: [documentId] } });
	}

	/**
	 * Libération à la fermeture de l'onglet : une requête Angular serait annulée, `fetch keepalive` part quand même.
	 * Sans garantie (coupure réseau, navigateur tué) : dans ce cas le verrou expire tout seul.
	 */
	libererAuDechargement(documentType: DocumentVerrouType, documentIds: number[], mode?: DocumentVerrouMode): void {
		const token = this.localStorage.get("token");
		if (!token || documentIds.length === 0) return;
		try {
			fetch(`${this.url}/liberer`, {
				method: "PUT",
				keepalive: true,
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				body: JSON.stringify({ data: { documentType, documentIds, mode } }),
			}).catch(() => {});
		} catch {
			// rien : le verrou expirera
		}
	}
}
